/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/

import { Tooltip as VTooltip } from '@veneer/core';
import React, { memo, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { generateID, isElementVisible } from '../../base/helper';
import { TooltipProps } from './types';

/**
 * UI Toolkit Tooltip component
 * @public
 */
const Tooltip: React.FC<TooltipProps> = ({
  variant = 'default',
  children,
  placement = 'bottom',
  id = generateID('clientos-ui-toolkit-tooltip'),
  tabIndex = 0,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const [placementProp, setPlacement] = useState(placement);
  const ref = useRef(null);

  useEffect(() => {
    const buttonElement: HTMLElement = ref.current;

    const setVisibility = (elem: Element) => {
      const viewportTtp = isElementVisible(elem);

      if (elem) {
        if (
          viewportTtp.right &&
          (placementProp.includes('right') ||
            placementProp.includes('trailing'))
        ) {
          setPlacement('bottom');
        }

        if (
          viewportTtp.left &&
          (placementProp.includes('left') || placementProp.includes('leading'))
        ) {
          setPlacement('bottom');
        }

        if (viewportTtp.bottom && placementProp.includes('bottom')) {
          setPlacement('top');
        }

        if (viewportTtp.top && placementProp.includes('top')) {
          setPlacement('bottom');
        }
      }
    };

    if (buttonElement) {
      const handleMouseEnter = () => {
        setPlacement(placement);
        const ttp = buttonElement.lastElementChild;
        setVisibility(ttp);
      };

      const handleMouseDown = () => {
        setPlacement(placement);
        const ttp = buttonElement.lastElementChild;
        setVisibility(ttp);
      };

      buttonElement.addEventListener('mouseenter', handleMouseEnter);
      buttonElement.addEventListener('mousedown', handleMouseDown);

      return () => {
        buttonElement.removeEventListener('mouseenter', handleMouseEnter);
        buttonElement.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [placement, placementProp]);

  return (
    <div
      ref={ref}
      className={`clientos-ui-toolkit-tooltip clientos-ui-toolkit-tooltip--${variant}`}
      id={id}
    >
      <VTooltip
        placement={placementProp}
        id={`${id}__content`}
        {...rest}
      >
        <div
          className="clientos-ui-toolkit-tooltip__child"
          id={`${id}__children`}
          tabIndex={tabIndex}
          aria-label={ariaLabel || rest.content.toString()}
        >
          {children}
        </div>
      </VTooltip>
    </div>
  );
};

export default memo(Tooltip);
